import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import Confirm from '@/components/Confirm';
import NumberInput from '@/components/NumberInput';
import useCheckboxes from '@/hooks/useCheckboxes';
import CartItemMoqAlert from '@/modules/store/components/CartItemMoqAlert';
import CartItemPromotionAlert from '@/modules/store/components/CartItemPromotionAlert';
import DiscountStar from '@/modules/store/components/DiscountStar';
import ImageThumbnail from '@/modules/store/components/ImageThumbnail';
import Price from '@/modules/store/components/Price';
import { CartDetails } from '@/modules/store/models/Cart';
import { ShopPath } from '@/modules/store/paths';
import { formatPrice } from '@/utils/currency';
import { getTaxLabels } from '@/utils/tax';
import { TrashIcon } from '@heroicons/react/24/outline';

interface CartTableProps {
  cart: CartDetails;
  onChangeOrderQuantity: (quantity: number, supplierId: string, itemId: number) => void;
  onChangeCheckedItems: (items: number[], supplierId: string) => void;
  removeItem: (supplierId: string, itemId: number) => void;
  showVat?: boolean;
  isOrderPredictionAvailable: boolean;
  countryCode?: string;
}

const CartTable = ({
  cart,
  onChangeOrderQuantity,
  onChangeCheckedItems,
  removeItem,
  showVat = true,
  isOrderPredictionAvailable = false,
  countryCode,
}: CartTableProps) => {
  const taxLabels = getTaxLabels(countryCode);
  const { checkboxes, handleMasterToggle, handleRowToggle, areAllChecked } = useCheckboxes<number>({
    checkedItems: cart.items,
    onChange: (items) => onChangeCheckedItems(items, cart.supplierId),
  });

  const handleOrderQuantityChange = (quantity: number, supplierId: string, itemId: number) => {
    onChangeOrderQuantity(quantity, supplierId, itemId);
  };

  const handleRemoveItem = (supplierId: string, itemId: number) => {
    removeItem(supplierId, itemId);
  };

  const formatPredictedSales = (value?: number | null): string => {
    return value ? Math.round(value).toString() : '';
  };

  const leftColSpan = 7 + (showVat ? 1 : 0) - 5;
  const rightColSpan = 7 + (isOrderPredictionAvailable ? 1 : 0) - 5;

  return (
    <div className="w-full">
      {/* Table for desktop/tablet */}
      <div className="hidden md:block table-container table-custom">
        <div className="table-container table-custom">
          <div className="table-wrapper">
            <table
              className="min-w-full w-[900px] lg:w-full table-fixed"
              data-testid="cart-items-table"
            >
              <thead>
                <tr className="bg-gray-50 border-b border-gray-300 text-gray-500 font-medium text-sm">
                  <th className="w-[6%] cell-checkbox min-w-14">
                    <Checkbox
                      data-testid="cart-supplier-select-all"
                      checked={areAllChecked()}
                      onChange={handleMasterToggle}
                    />
                  </th>
                  <th className="w-[32%] px-6 py-3 text-left text-sm">SKU</th>
                  <th className="w-[12%] px-3 py-3 text-left">{taxLabels.priceExcluding}</th>
                  {showVat && <th className="w-[8%] px-3 py-3 text-left">{taxLabels.rate}</th>}
                  {isOrderPredictionAvailable && (
                    <th className="w-[10%] px-3 py-3 text-left">Predicted Sales</th>
                  )}
                  <th className="w-[12%] lg:w-[12%] px-3 py-3 text-left">Order Quantity</th>
                  <th className="w-[14%] px-3 py-3 text-right">{taxLabels.subtotalExcluding}</th>
                  <th className="w-[6%]"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {cart.items.map((item, index) => (
                  <tr
                    key={item.skuId + index}
                    className="text-sm lg:text-base"
                    data-testid="cart-item-row"
                    data-item-id={item.id}
                    data-sku-id={item.skuId}
                    data-sku-name={item.sku.name}
                    data-supplier-id={item.supplierId}
                  >
                    <td className="table-cell-checkbox">
                      <Checkbox
                        data-testid="cart-item-select"
                        checked={checkboxes.checked(item.id)}
                        onChange={() => handleRowToggle(item.id)}
                      />
                    </td>
                    <td className="px-1 pr-3 py-1">
                      <div className="flex gap-4 items-center">
                        {item.sku.image && (
                          <div className="relative pr-4">
                            {item.unitPrice && <DiscountStar price={item.unitPrice} />}
                            <ImageThumbnail image={item.sku.image} />
                          </div>
                        )}

                        <div>
                          <div className="font-medium">{item.sku.name}</div>

                          <div className="mt-1 space-y-1">
                            <CartItemMoqAlert item={item} />
                            <CartItemPromotionAlert item={item} />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-1">
                      <Price price={item.unitPrice} />
                    </td>
                    {showVat && (
                      <td className="px-3 py-1">{item.vatRate ? item.vatRate + '%' : '0%'}</td>
                    )}
                    {isOrderPredictionAvailable && (
                      <td className="px-3 py-1">
                        {formatPredictedSales(item.prediction?.predictedSales)}
                      </td>
                    )}
                    <td className="px-3 py-1">
                      <Confirm
                        onConfirm={() => handleRemoveItem(item.supplierId, item.id)}
                        message={`Do you want to delete your order of ${item.sku.name}?`}
                      >
                        {(handleClick) => (
                          <NumberInput
                            data-testid="cart-item-quantity-input"
                            value={item.quantity}
                            min={0}
                            max={1_000_000}
                            onChange={(quantity: number) => {
                              if (quantity < 1) {
                                handleClick();
                              }
                              handleOrderQuantityChange(quantity, item.supplierId, item.id);
                            }}
                          />
                        )}
                      </Confirm>
                    </td>
                    <td className="px-3 py-1 text-right">{formatPrice(item.lineItemPrice)}</td>
                    <td className="px-3 py-1">
                      <div className="flex items-center justify-center">
                        <Confirm
                          onConfirm={() => handleRemoveItem(item.supplierId, item.id)}
                          message={`Do you want to delete your order of ${item.sku.name}?`}
                        >
                          {(handleClick, loading) => (
                            <Button
                              data-testid="cart-item-remove"
                              icon={
                                <TrashIcon className="w-5 h-5 cursor-pointer text-black hover:text-red-500 transition duration-300" />
                              }
                              onClick={handleClick}
                              variant="plain"
                              loading={loading}
                            />
                          )}
                        </Confirm>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tbody className="border-t border-gray-300">
                <tr>
                  <td colSpan={leftColSpan} rowSpan={3} className="px-3 py-1">
                    <div>
                      <Button to={ShopPath.withSupplierId(cart.supplierId)} variant="primary">
                        Add More Items
                      </Button>
                    </div>
                  </td>
                  <td
                    colSpan={rightColSpan}
                    className="text-right px-6 py-1 pt-6 font-semibold text-sm"
                  >
                    {taxLabels.subtotalExcluding}
                  </td>
                  <td className="px-3 py-1 pt-6 text-right">
                    {formatPrice(cart.subtotal, cart.subtotal?.currencyCode)}
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td
                    colSpan={rightColSpan}
                    className={`text-right px-6 py-1 font-semibold text-sm ${!showVat && 'pb-6'}`}
                  >
                    {taxLabels.totalExcluding}
                  </td>
                  <td className={`px-3 py-1 text-right ${!showVat && 'pb-6'}`}>
                    {formatPrice(cart.subtotal, cart.subtotal?.currencyCode)}
                  </td>
                  <td></td>
                </tr>
                {showVat && (
                  <tr>
                    <td
                      colSpan={rightColSpan}
                      className="text-right px-6 py-1 pb-6 font-semibold text-sm"
                    >
                      {taxLabels.grandTotalIncluding}
                    </td>
                    <td className="px-3 py-1 pb-6 text-right">
                      {formatPrice(cart.grandTotal, cart.grandTotal?.currencyCode)}
                    </td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Card layout for mobile */}
      <div className="md:hidden space-y-4">
        {/* Select All checkbox */}
        <div className="flex items-center justify-between px-1">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Checkbox
              data-testid="cart-supplier-select-all"
              checked={areAllChecked()}
              onChange={handleMasterToggle}
            />
            Select All
          </label>
        </div>

        {cart.items.map((item) => (
          <div
            key={item.id}
            className="bg-white shadow-sm rounded-xl p-4 space-y-3 border"
            data-testid="cart-item-card"
            data-item-id={item.id}
            data-sku-id={item.skuId}
            data-sku-name={item.sku.name}
            data-supplier-id={item.supplierId}
          >
            <div className="flex items-start gap-4">
              {/* Checkbox on the left */}
              <div className="pt-1">
                <Checkbox
                  data-testid="cart-item-select"
                  checked={checkboxes.checked(item.id)}
                  onChange={() => handleRowToggle(item.id)}
                />
              </div>

              {/* Image */}
              {item.sku.image?.url && (
                <div className="relative pr-4">
                  {item.unitPrice && <DiscountStar price={item.unitPrice} />}
                  <ImageThumbnail image={item.sku.image} />
                </div>
              )}

              {/* Product Info */}
              <div className="flex-1 grid grid-cols-2 gap-y-1 text-sm">
                <div className="col-span-2 text-base font-semibold">{item.sku.name}</div>

                <div className="col-span-2">
                  <CartItemMoqAlert item={item} />
                  <CartItemPromotionAlert item={item} />
                </div>

                <div>Code:</div>
                <div>{item.sku.externalCode}</div>

                {showVat && (
                  <>
                    <div>{taxLabels.rate}:</div>
                    <div>{item.vatRate ? `${item.vatRate}%` : '0%'}</div>
                  </>
                )}

                {item.prediction?.predictedSales !== undefined && (
                  <>
                    <div>Predicted Sales:</div>
                    <div>{formatPredictedSales(item.prediction?.predictedSales)}</div>
                  </>
                )}

                <div>Unit Price:</div>
                <div className="flex space-x-4">
                  <Price price={item.unitPrice} />
                </div>

                <div>{taxLabels.subtotalExcluding}:</div>
                <div className="font-medium">{formatPrice(item.lineItemPrice)}</div>
              </div>
            </div>

            <div className="flex gap-4 items-center">
              <Confirm
                onConfirm={() => handleRemoveItem(item.supplierId, item.id)}
                message={`Do you want to delete your order of ${item.sku.name}?`}
              >
                {(handleClick) => (
                  <div className="w-1/2">
                    <NumberInput
                      data-testid="cart-item-quantity-input"
                      className="w-full"
                      value={item.quantity}
                      min={0}
                      max={1_000_000}
                      onChange={(quantity) => {
                        if (quantity < 1) {
                          handleClick();
                        }
                        handleOrderQuantityChange(quantity, item.supplierId, item.id);
                      }}
                    />
                  </div>
                )}
              </Confirm>
              <Confirm
                onConfirm={() => handleRemoveItem(item.supplierId, item.id)}
                message={`Do you want to delete your order of ${item.sku.name}?`}
              >
                {(handleClick, loading) => (
                  <Button
                    data-testid="cart-item-remove"
                    icon={
                      <TrashIcon className="w-5 h-5 text-gray-700 hover:text-red-500 transition" />
                    }
                    onClick={handleClick}
                    variant="plain"
                    loading={loading}
                  />
                )}
              </Confirm>
            </div>
          </div>
        ))}

        {/* Totals summary card */}
        <div className="bg-white shadow-sm rounded-xl p-4 space-y-3 border text-sm">
          <div className="flex justify-between">
            <span className="font-medium">{taxLabels.subtotalExcluding}:</span>
            <span>{formatPrice(cart.subtotal, cart.subtotal?.currencyCode)}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium">{taxLabels.totalExcluding}:</span>
            <span>{formatPrice(cart.subtotal, cart.subtotal?.currencyCode)}</span>
          </div>
          {showVat && (
            <div className="flex justify-between">
              <span className="font-medium">{taxLabels.grandTotalIncluding}:</span>
              <span>{formatPrice(cart.grandTotal, cart.grandTotal?.currencyCode)}</span>
            </div>
          )}
          <div className="pt-3">
            <Button
              to={ShopPath.withSupplierId(cart.supplierId)}
              variant="primary"
              className="w-full"
            >
              Add More Items
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartTable;
